<template>
  <div class="card mb-4 aids-panel">
    <div class="card-header d-flex justify-content-between align-items-center">
      <h5 class="card-title mb-0">Player Aids</h5>
      <div class="d-flex gap-1">
        <button
          v-if="aids.length"
          class="btn btn-sm btn-outline-secondary"
          :disabled="printing"
          @click="printAids"
        >{{ printing ? 'Preparing…' : 'Print' }}</button>
        <button
          v-if="canEdit"
          class="btn btn-sm btn-success"
          @click="openAdd"
        >Add aid</button>
      </div>
    </div>

    <div class="card-body">
      <p v-if="!aids.length && !loading" class="text-muted mb-0 small">
        Turn summaries, setup diagrams, scoring references — anything a player
        holds that is not a card and not the board.
        <span v-if="canEdit">They print one per player, not one per deck.</span>
      </p>

      <div v-if="loading" class="text-center py-3">
        <div class="spinner-border spinner-border-sm" role="status">
          <span class="visually-hidden">Loading aids…</span>
        </div>
      </div>

      <ul v-else-if="aids.length" class="aid-list">
        <li v-for="aid in aids" :key="aid.id" class="aid-row">
          <span class="aid-source" :data-source="aid.source">{{ sourceLabel(aid.source) }}</span>
          <span class="aid-body">
            <router-link :to="`/aids/${aid.id}`" class="aid-name">{{ aid.name }}</router-link>
            <span v-if="aid.description" class="aid-desc">{{ aid.description }}</span>
          </span>
          <span class="aid-copies" :title="`Prints ${aid.copies} sheet(s)`">
            ×{{ aid.copies }}
          </span>
          <button
            v-if="canEdit"
            class="btn btn-sm btn-link text-danger p-0 aid-remove"
            :disabled="busy"
            @click="remove(aid)"
          >Remove</button>
        </li>
      </ul>

      <p v-if="error" class="text-danger small mb-0 mt-2">{{ error }}</p>
    </div>

    <!-- Teleported for the same reason every other modal here is: the routed
         page is a stacking context, so a dialog left inside it renders under
         Bootstrap's body-level backdrop. -->
    <Teleport to="body">
      <div v-if="adding" class="aid-overlay" @click.self="adding = false">
        <div class="aid-dialog">
          <h5 class="mb-3">Add a player aid</h5>

          <div class="mb-3">
            <label class="form-label small" for="aidName">Name</label>
            <input id="aidName" v-model="draft.name" class="form-control" maxlength="200"
                   placeholder="Turn Summary" />
          </div>

          <div class="mb-3">
            <label class="form-label small">Where does it come from?</label>
            <div class="btn-group w-100" role="group">
              <button
                v-for="s in sources"
                :key="s.value"
                type="button"
                class="btn btn-sm"
                :class="draft.source === s.value ? 'btn-primary' : 'btn-outline-primary'"
                @click="draft.source = s.value"
              >{{ s.label }}</button>
            </div>
            <div class="form-text">{{ sourceHint }}</div>
          </div>

          <div v-if="draft.source === 'markdown'" class="mb-3">
            <label class="form-label small" for="aidBody">Text</label>
            <textarea id="aidBody" v-model="draft.body" class="form-control" rows="6"
                      maxlength="4000"
                      placeholder="1. Draw a Wind card.&#10;2. Move up to your Speed.&#10;3. Trade or Explore."></textarea>
          </div>

          <div v-else-if="draft.source === 'upload'" class="mb-3">
            <label class="form-label small" for="aidFile">Image or PDF</label>
            <input id="aidFile" ref="fileInput" type="file" class="form-control"
                   accept="image/*,application/pdf" @change="onFile" />
            <div v-if="uploading" class="form-text">Uploading…</div>
            <div v-else-if="draft.fileUrl" class="form-text text-success">Uploaded.</div>
          </div>

          <div v-else class="mb-3">
            <label class="form-label small" for="aidCard">Which card?</label>
            <select id="aidCard" v-model="draft.cardId" class="form-select">
              <option value="">Choose a card…</option>
              <option v-for="c in cards" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
            <div class="form-text">
              It stops being dealt with the deck and starts being an aid.
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label small" for="aidCopies">Copies to print</label>
            <input id="aidCopies" v-model.number="draft.copies" type="number" min="1" max="12"
                   class="form-control" style="max-width: 8rem" />
            <div class="form-text">One per player is typical.</div>
          </div>

          <p v-if="addError" class="text-danger small">{{ addError }}</p>

          <div class="d-flex gap-2 justify-content-end">
            <button class="btn btn-outline-secondary btn-sm" @click="adding = false">Cancel</button>
            <button class="btn btn-primary btn-sm" :disabled="busy || uploading" @click="save">
              {{ busy ? 'Adding…' : 'Add aid' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { sparkforgeApi } from '@/sparkforgeApi';
import { storage } from '@/firebase';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

const SOURCES = [
  { value: 'markdown', label: 'Write it' },
  { value: 'upload', label: 'Upload' },
  { value: 'card', label: 'From a card' }
];

const HINTS = {
  markdown: 'Typed here and set by the same renderer as your cards, so it matches.',
  upload: 'You already made it elsewhere — an image or a PDF.',
  card: 'Promote a card you have already designed.'
};

function blankDraft() {
  return { name: '', source: 'markdown', body: '', fileUrl: '', contentType: '',
           cardId: '', copies: 1 };
}

export default {
  name: 'GameAidsPanel',
  props: {
    gameId: { type: String, required: true },
    canEdit: { type: Boolean, default: false }
  },
  emits: ['changed'],
  data() {
    return {
      aids: [],
      cards: [],
      sources: SOURCES,
      draft: blankDraft(),
      adding: false,
      loading: true,
      busy: false,
      uploading: false,
      printing: false,
      error: null,
      addError: null
    };
  },
  computed: {
    sourceHint() {
      return HINTS[this.draft.source];
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    sourceLabel(source) {
      return { markdown: 'written', upload: 'file', card: 'card' }[source] || source;
    },
    async printAids() {
      // One sheet per copy: an aid with copies=4 prints four times, which is
      // the difference between an aid and a card.
      this.printing = true;
      this.error = null;
      try {
        const exported = await sparkforgeApi.exportPdf(this.gameId, 'aids');
        window.open(exported.url, '_blank', 'noopener');
      } catch (e) {
        this.error = e.message || 'The aids could not be printed.';
      } finally {
        this.printing = false;
      }
    },
    async load() {
      this.loading = true;
      try {
        const result = await sparkforgeApi.listAids(this.gameId);
        this.aids = result.data || [];
      } catch (e) {
        this.error = e.message || 'Player aids could not be loaded.';
      } finally {
        this.loading = false;
      }
    },
    async openAdd() {
      this.draft = blankDraft();
      this.addError = null;
      this.adding = true;
      // Only needed for the "from a card" source, but loading it now means the
      // dropdown is populated the moment someone picks that option.
      if (!this.cards.length) {
        try {
          const result = await sparkforgeApi.listCards(this.gameId);
          this.cards = result.data || [];
        } catch (e) {
          this.cards = [];
        }
      }
    },
    async onFile(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      this.uploading = true;
      this.addError = null;
      try {
        const path = `aids/${this.gameId}/${Date.now()}_${file.name}`;
        const snapshot = await uploadBytes(storageRef(storage, path), file);
        this.draft.fileUrl = await getDownloadURL(snapshot.ref);
        this.draft.contentType = file.type;
        if (!this.draft.name) this.draft.name = file.name.replace(/\.[^.]+$/, '');
      } catch (e) {
        this.addError = 'That file could not be uploaded.';
      } finally {
        this.uploading = false;
      }
    },
    async save() {
      this.busy = true;
      this.addError = null;
      try {
        const aid = {
          name: this.draft.name,
          source: this.draft.source,
          copies: this.draft.copies || 1
        };
        if (this.draft.source === 'markdown') aid.body = this.draft.body;
        if (this.draft.source === 'upload') {
          aid.fileUrl = this.draft.fileUrl;
          if (this.draft.contentType) aid.contentType = this.draft.contentType;
        }
        if (this.draft.source === 'card') aid.cardId = this.draft.cardId;

        await sparkforgeApi.createAids(this.gameId, [aid]);
        this.adding = false;
        await this.load();
        this.$emit('changed');
      } catch (e) {
        // The API names the missing field, which is more useful than a generic
        // "could not save" — pass it straight through.
        this.addError = e.message || 'That aid could not be added.';
      } finally {
        this.busy = false;
      }
    },
    async remove(aid) {
      if (!window.confirm(`Remove "${aid.name}"?`)) return;
      this.busy = true;
      try {
        await sparkforgeApi.deleteAid(aid.id);
        await this.load();
        this.$emit('changed');
      } catch (e) {
        this.error = e.message || 'That aid could not be removed.';
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>

<style scoped>
.aid-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.4rem; }
.aid-row {
  display: flex;
  /* Same reason as a board's space rows: without this the row grows to fit its
     one-line description and pushes past the card. */
  min-width: 0;
  align-items: center;
  gap: 0.7rem;
  padding: 0.5rem 0.6rem;
  border: 1px solid var(--border-sketch, #e3e0d9);
  border-radius: 8px;
  background: var(--bg-surface, #fff);
}
.aid-source {
  font-family: monospace;
  font-size: 0.66rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.15rem 0.45rem;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.05);
  color: var(--ink-light, #7a7a7a);
  flex: 0 0 auto;
}
.aid-body { flex: 1; min-width: 0; }
.aid-name {
  display: block;
  font-weight: 600;
  font-size: 0.9rem;
  /* The description already truncated; the name did not, so a long aid name
     could not shrink and pushed the whole row past the card edge on a phone. */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.aid-desc {
  display: block;
  font-size: 0.78rem;
  color: var(--ink-light, #7a7a7a);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.aid-copies {
  flex: 0 0 auto;
  font-variant-numeric: tabular-nums;
  font-size: 0.8rem;
  color: var(--ink-light, #7a7a7a);
}
.aid-remove { flex: 0 0 auto; font-size: 0.78rem; }

/* On a narrow screen four items on one line leaves the name a few characters.
   Let the row wrap so the name gets a full line instead of an ellipsis. */
@media (max-width: 575.98px) {
  .aid-row { flex-wrap: wrap; gap: 0.35rem 0.6rem; }
  .aid-body { flex: 1 0 100%; order: 1; }
  .aid-source { order: 0; }
  .aid-copies { order: 2; margin-left: auto; }
  .aid-remove { order: 3; }
  .aid-name { white-space: normal; overflow-wrap: anywhere; }
}

.aid-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 1060;
}
.aid-dialog {
  background: var(--bg-surface, #fff);
  border-radius: 12px;
  padding: 1.25rem;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.28);
}
</style>
